import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { CheckCircle2, AlertCircle } from 'lucide-react';

const DemoModal = ({ open, onOpenChange }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Send email via backend API
      const response = await fetch('http://localhost:8000/api/demo-request', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        throw new Error('Failed to submit demo request');
      }

      setSubmitted(true);
      
      // Reset after 3 seconds
      setTimeout(() => {
        setSubmitted(false);
        setFormData({
          name: '',
          email: '',
          company: '',
          message: ''
        });
        onOpenChange(false);
      }, 3000);
    } catch (err) {
      setError(err.message || 'Failed to submit. Please try again.');
      console.error('Demo request error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSubmitted(false);
    setError('');
    setFormData({
      name: '',
      email: '',
      company: '',
      message: ''
    });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="bg-slate-900 border-slate-700 text-white sm:max-w-[500px]">
        {!submitted ? (
          <>
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold text-white">
                Schedule a Demo
              </DialogTitle>
              <DialogDescription className="text-slate-400">
                Fill out the form below and our team will get back to you within 24 hours.
              </DialogDescription>
            </DialogHeader>
            {error && (
              <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded-md">
                <AlertCircle className="h-4 w-4 text-red-400" />
                <span className="text-sm text-red-400">{error}</span>
              </div>
            )}
            <form onSubmit={handleSubmit} className="space-y-5 mt-4">
              <div className="space-y-2">
                <Label htmlFor="name" className="text-slate-300">
                  Full Name *
                </Label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  disabled={loading}
                  placeholder="John Doe"
                  className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 focus:border-teal-500 disabled:opacity-50"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email" className="text-slate-300">
                  Work Email *
                </Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  disabled={loading}
                  placeholder="john@company.com"
                  className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 focus:border-teal-500 disabled:opacity-50"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="company" className="text-slate-300">
                  Company *
                </Label>
                <Input
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  required
                  disabled={loading}
                  placeholder="Your Company"
                  className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 focus:border-teal-500 disabled:opacity-50"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="message" className="text-slate-300">
                  Message (Optional)
                </Label>
                <Textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  disabled={loading}
                  placeholder="Tell us about your needs..."
                  rows={4}
                  className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 focus:border-teal-500 resize-none disabled:opacity-50"
                />
              </div>
              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleClose}
                  disabled={loading}
                  className="flex-1 border-slate-700 text-slate-300 hover:bg-slate-800 disabled:opacity-50"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={loading}
                  className="flex-1 bg-teal-500 hover:bg-teal-400 text-slate-950 font-semibold disabled:opacity-50"
                >
                  {loading ? 'Submitting...' : 'Submit Request'}
                </Button>
              </div>
            </form>
          </>
        ) : (
          <div className="py-12 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-teal-500/10 mb-6 border border-teal-500/30">
              <CheckCircle2 className="h-8 w-8 text-teal-400" />
            </div>
            <h3 className="text-2xl font-bold text-white mb-3">
              Request Submitted!
            </h3>
            <p className="text-slate-400">
              Thank you for your interest. Our team will contact you shortly.
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default DemoModal;
